import { useEffect, useRef } from "react";
import * as d3 from "d3";

interface CostComparisonBarChartProps {
  withFownd: number;
  withoutFownd: number;
}

const CostComparisonBarChart = ({
  withFownd,
  withoutFownd,
}: CostComparisonBarChartProps) => {
  const chartRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (chartRef.current) {
      d3.select(chartRef.current).selectAll("*").remove();

      const data = [
        { label: "Without Fownd", value: withoutFownd },
        { label: "With Fownd", value: withFownd },
      ];

      const margin = { top: 30, right: 30, bottom: 50, left: 60 };
      const width = 400 - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;

      const svg = d3
        .select(chartRef.current)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const x = d3
        .scaleBand()
        .domain(data.map((d) => d.label))
        .range([0, width])
        .padding(0.4);

      svg
        .append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x))
        .selectAll("text")
        .style("font-size", "12px");

      const y = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => d.value) || 0])
        .range([height, 0])
        .nice();

      svg.append("g").call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickFormat((d) => `$${(d as number) / 1000}k`)
      );

      svg
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", width / 2)
        .attr("y", height + margin.bottom - 10)
        .text("Scenario");

      svg
        .append("text")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .attr("y", -margin.left + 20)
        .attr("x", -height / 2)
        .text("Annual Cost");

      const colorScale = d3
        .scaleOrdinal<string>()
        .domain(["Without Fownd", "With Fownd"])
        .range(["#475569", "#1e3d3b"]);

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "d3-tooltip")
        .style("position", "absolute")
        .style("z-index", "10")
        .style("visibility", "hidden")
        .style("background", "rgba(0,0,0,0.75)")
        .style("color", "#fff")
        .style("padding", "8px 12px")
        .style("border-radius", "4px")
        .style("font-size", "14px");

      svg
        .selectAll(".bar")
        .data(data)
        .enter()
        .append("rect")
        .attr("class", "bar")
        .attr("x", (d) => x(d.label)!)
        .attr("y", (d) => y(d.value))
        .attr("width", x.bandwidth())
        .attr("height", (d) => height - y(d.value))
        .attr("fill", (d) => colorScale(d.label))
        .on("mouseover", function (event, d) {
          d3.select(this).attr("opacity", 0.8);
          tooltip
            .style("visibility", "visible")
            .text(`${d.label}: $${d.value.toLocaleString()}`);
        })
        .on("mousemove", function (event) {
          tooltip
            .style("top", event.pageY - 10 + "px")
            .style("left", event.pageX + 10 + "px");
        })
        .on("mouseout", function () {
          d3.select(this).attr("opacity", 1);
          tooltip.style("visibility", "hidden");
        });
    }
  }, [withFownd, withoutFownd]);

  return (
    <div className="flex flex-col items-center">
      <h4 className="text-lg font-semibold text-slate-700 mb-2">
        Cost Comparison
      </h4>
      <svg ref={chartRef}></svg>
      <p className="text-xs text-slate-500 mt-2">
        Cost Comparison: Lower is Better
      </p>
    </div>
  );
};

export default CostComparisonBarChart; 